/** Settings: General (business, account, team), WhatsApp click-to-chat, e-mail alert preferences and their History. */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorAlert, FullPageSpinner, cx } from "@/components/ui";
import { identity } from "@/lib/api";

import { BrandingTab } from "./Branding";
import { GeneralTab } from "./General";
import { HistoryTab } from "./History";
import { NotificationsTab } from "./Notifications";
import { WhatsAppTab } from "./WhatsApp";
import { SocialTab } from "./Social";

export { describeAgent } from "./General";

type Tab = "general" | "branding" | "whatsapp" | "social" | "notifications" | "history";
const TABS: [Tab, string][] = [
  ["general", "General"],
  ["branding", "Branding"],
  ["whatsapp", "WhatsApp"],
  ["social", "Social"],
  ["notifications", "Notifications"],
  // Only shown to people who can read the audit log (Owner, Business Administrator).
  ["history", "History"],
];

export function SettingsPage() {
  const { current, can } = useOrganization();
  const tabs = TABS.filter(([t]) => t !== "history" || can("audit:read"));
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some(([t]) => t === params.get("tab")) ? (params.get("tab") as Tab) : "general";
  const me = useQuery({ queryKey: ["me"], queryFn: identity.me });
  const subtitle = {
    general: "Business profile, account, team access and notifications",
    branding: "Your logo and brand colour, kept separate and versioned per business",
    whatsapp: "Let customers message you on WhatsApp from your website, cards and shop window",
    social: "Keep your social links and profile handles in one place",
    notifications: `Choose which alerts you get for ${current.organization.name}, and where`,
    history: "Who changed these settings, when, and what they were before",
  }[tab];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3.5">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#f3ebe7] text-[#443e3b] shadow-[0_2px_5px_rgba(0,0,0,0.05)]">
          <span
            aria-hidden
            className="block size-5 bg-current"
            style={{
              maskImage: "url(/icons/settings.svg)",
              WebkitMaskImage: "url(/icons/settings.svg)",
              maskSize: "100% 100%",
              WebkitMaskSize: "100% 100%",
            }}
          />
        </span>
        <div className="flex min-w-0 flex-col gap-[5px]">
          <h1 className="text-[28px] leading-[34px] font-semibold tracking-[-0.3px] text-fg">Settings</h1>
          <p className="text-sm text-fg-subtle">{subtitle}</p>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Settings"
        className="flex gap-1.5 self-start rounded-xl border border-border bg-brand-50 p-1"
      >
        {tabs.map(([t, label]) => (
          <button
            key={t}
            role="tab"
            type="button"
            aria-selected={tab === t}
            onClick={() => setParams(t === "general" ? {} : { tab: t }, { replace: true })}
            className={cx(
              "rounded-[9px] px-3.5 py-2 text-[13.5px] font-semibold",
              tab === t
                ? "bg-surface text-fg shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                : "text-fg-subtle hover:text-fg",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "general" &&
        (me.isPending ? (
          <FullPageSpinner />
        ) : me.isError ? (
          <ErrorAlert error={me.error} />
        ) : (
          <GeneralTab account={me.data} />
        ))}
      {tab === "branding" && <BrandingTab />}
      {tab === "whatsapp" && <WhatsAppTab />}
      {tab === "social" && <SocialTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "history" && <HistoryTab />}
    </div>
  );
}
